import { useEffect } from "react";
import { TrendingUp, Clock, CheckCircle, Award, Zap, Target, MessageCircle, RefreshCw, Loader2, ShieldCheck, Activity } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { useAgent } from "@/contexts/AgentContext";
import { useAgentPerformance } from "@/hooks/useAgentPerformance";

// Scoring benchmarks — thresholds agents are measured against
const BENCHMARKS = {
  responseTimeMin: 5,       // minutes — "good" if <= this
  leadsWorkedMin: 50,       // percent — "good" if >= this
  closeRateMin: 10,         // percent — "good" if >= this
  leadsMonthTarget: 50,     // leads/month target
} as const;

const tierConfig = {
  Bronze: { label: "Bronze", color: "text-orange-600", bg: "bg-orange-500/10", border: "border-orange-500/20", min: 0, max: 49 },
  Silver: { label: "Silver", color: "text-slate-500", bg: "bg-slate-500/10", border: "border-slate-500/20", min: 50, max: 69 },
  Gold: { label: "Gold", color: "text-accent", bg: "bg-accent/10", border: "border-accent/20", min: 70, max: 89 },
  Elite: { label: "Elite", color: "text-purple-600", bg: "bg-purple-500/10", border: "border-purple-500/20", min: 90, max: 100 },
};

// Format response time in human-readable units
function formatResponseTime(minutes: number): string {
  if (minutes <= 0) return "—";
  if (minutes < 1) return `${Math.round(minutes * 60)}s`;
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  const hours = minutes / 60;
  if (hours < 24) return `${hours.toFixed(1)} hrs`;
  const days = hours / 24;
  return `${days.toFixed(1)} days`;
}

const Performance = () => {
  const { state } = useAgent();
  const { profile } = state;
  const { recalculate, loading: recalculating, result } = useAgentPerformance();

  // Recalculate on mount to show fresh metrics
  useEffect(() => {
    if (state.loaded && profile.id) {
      recalculate();
    }
  }, [state.loaded, profile.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const tier = tierConfig[profile.tier];

  // ALWAYS use RPC result when available to avoid stale AgentContext data.
  const score = result?.performanceScore ?? state.performance.performanceScore;
  const avgResponseTime = result?.avgResponseTime ?? state.performance.avgResponseTime;
  const leadsWorkedRate = result?.contactRate ?? state.performance.contactRate;
  const closeRate = result?.closeRate ?? state.performance.closeRate;
  const totalLeadsInWindow = result?.totalLeadsInWindow ?? 0;
  const contactedLeads = result?.contactedLeads ?? 0;
  const wonLeads = result?.wonLeads ?? 0;

  // Sub-scores and weights from v2 RPC
  const rtScore = result?.responseTimeScore ?? 0;
  const lwScore = result?.contactRateScore ?? leadsWorkedRate;
  const crScore = result?.closeRateScore ?? 0;
  const rtWeight = result?.responseTimeWeight ?? 30;
  const lwWeight = result?.contactRateWeight ?? 55;
  const crWeight = result?.closeRateWeight ?? 15;
  const percentile = result?.percentile ?? 0;
  const totalAgents = result?.totalAgents ?? 1;

  // Score breakdown data for visualization
  const breakdown = [
    { label: "Response Time", score: Math.round(rtScore), weight: rtWeight, color: "bg-primary" },
    { label: "Leads Worked", score: Math.round(lwScore), weight: lwWeight, color: "bg-accent" },
    { label: "Close Rate", score: Math.round(crScore), weight: crWeight, color: "bg-green-600" },
  ];

  const metrics = [
    {
      label: "Avg. Response Time",
      value: formatResponseTime(avgResponseTime),
      benchmark: `< ${BENCHMARKS.responseTimeMin} min`,
      status: avgResponseTime > 0 && avgResponseTime <= BENCHMARKS.responseTimeMin ? "good" : avgResponseTime === 0 ? "neutral" : "needs_work",
      icon: Clock,
      progress: rtScore,
      detail: "Time between receiving a lead and your first action",
      hint: "Faster responses convert significantly better — under 5 min is ideal",
    },
    {
      label: "Leads Worked",
      value: totalLeadsInWindow > 0 ? `${leadsWorkedRate.toFixed(0)}%` : "—",
      benchmark: `> ${BENCHMARKS.leadsWorkedMin}%`,
      status: leadsWorkedRate >= BENCHMARKS.leadsWorkedMin ? "good" : totalLeadsInWindow === 0 ? "neutral" : "needs_work",
      icon: Activity,
      progress: lwScore,
      detail: totalLeadsInWindow > 0 ? `${contactedLeads} of ${totalLeadsInWindow} leads` : "No leads yet",
      hint: "Any status update counts — contacted, no answer, qualified, or won",
    },
    {
      label: "Close Rate",
      value: totalLeadsInWindow > 0 ? `${closeRate.toFixed(1)}%` : "—",
      benchmark: `> ${BENCHMARKS.closeRateMin}%`,
      status: closeRate >= BENCHMARKS.closeRateMin ? "good" : totalLeadsInWindow === 0 ? "neutral" : "needs_work",
      icon: CheckCircle,
      progress: crScore,
      detail: totalLeadsInWindow > 0 ? `${wonLeads} won of ${totalLeadsInWindow} leads` : "No leads yet",
      hint: "Percentage of your leads that reach Won status",
    },
    {
      label: "Leads (30 days)",
      value: `${totalLeadsInWindow}`,
      benchmark: `Target: ${BENCHMARKS.leadsMonthTarget}+`,
      status: totalLeadsInWindow >= BENCHMARKS.leadsMonthTarget ? "good" : totalLeadsInWindow === 0 ? "neutral" : "needs_work",
      icon: Target,
      progress: Math.min(100, (totalLeadsInWindow / BENCHMARKS.leadsMonthTarget) * 100),
      detail: "Total leads delivered in the last 30 days",
      hint: "This is determined by your contract — not by your performance score",
    },
  ];

  const tips = [
    {
      icon: Zap,
      title: "Respond faster",
      description: "Leads contacted within 5 minutes have 21x higher conversion. Update their status as soon as you take action.",
    },
    {
      icon: Target,
      title: "Work every lead",
      description: "Any status update counts — even marking a lead as No Answer shows you're working it. This is the biggest factor in your score.",
    },
    {
      icon: MessageCircle,
      title: "Follow up consistently",
      description: "80% of sales require 5+ follow-ups. Use the notes and follow-up date features to stay on track.",
    },
    {
      icon: Award,
      title: "Climb the tiers",
      description: "Higher tiers unlock priority access when new leads become available. Your tier is based on your last 30 days, so improvement is always reflected.",
    },
  ];

  // Calculate next tier progress
  const getNextTierInfo = () => {
    if (score >= 90) return { next: "Elite", needed: 0, progress: 100 };
    if (score >= 70) return { next: "Elite", needed: 90 - score, progress: ((score - 70) / 20) * 100 };
    if (score >= 50) return { next: "Gold", needed: 70 - score, progress: ((score - 50) / 20) * 100 };
    return { next: "Silver", needed: 50 - score, progress: (score / 50) * 100 };
  };

  const nextTierInfo = getNextTierInfo();

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">Performance</h1>
            <p className="text-sm text-muted-foreground">
              Based on your last 30 days of activity. Updates automatically as you work your leads.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => recalculate()}
            disabled={recalculating}
            className="gap-2 w-full sm:w-auto"
          >
            {recalculating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Refresh
          </Button>
        </div>

        {/* Score & Tier Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Performance Score */}
          <div className="bg-card rounded-2xl border border-border p-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
            <div className="relative">
              <p className="text-sm font-medium text-muted-foreground mb-4 text-center">Performance Score</p>
              <div className="flex justify-center">
                <div className="relative inline-flex items-center justify-center w-40 h-40">
                  <svg className="w-full h-full -rotate-90">
                    <circle
                      cx="80"
                      cy="80"
                      r="72"
                      fill="none"
                      stroke="hsl(var(--muted))"
                      strokeWidth="10"
                    />
                    <circle
                      cx="80"
                      cy="80"
                      r="72"
                      fill="none"
                      stroke="hsl(var(--accent))"
                      strokeWidth="10"
                      strokeLinecap="round"
                      strokeDasharray={`${(score / 100) * 452} 452`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-4xl font-bold text-foreground">{score}</span>
                    <span className="text-xs text-muted-foreground">out of 100</span>
                  </div>
                </div>
              </div>

              {/* Percentile — only shown when there are enough agents for meaningful comparison */}
              <div className="text-center mt-3">
                {totalAgents >= 5 ? (
                  <p className="text-sm font-medium text-accent">
                    Better than {percentile}% of agents
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {totalLeadsInWindow > 0
                      ? `Based on ${totalLeadsInWindow} lead${totalLeadsInWindow !== 1 ? "s" : ""} in the last 30 days`
                      : "Your score will update as you work leads"}
                  </p>
                )}
              </div>

              {/* Score Breakdown */}
              <div className="mt-5 pt-4 border-t border-border">
                <p className="text-xs font-medium text-muted-foreground mb-3">Score Breakdown</p>
                <div className="space-y-2.5">
                  {breakdown.map((item) => (
                    <div key={item.label} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-24 flex-shrink-0">{item.label}</span>
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${item.color}`}
                          style={{ width: `${item.score}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-foreground w-7 text-right">{item.score}</span>
                      <span className="text-xs text-muted-foreground w-10 text-right">{item.weight}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Current Tier */}
          <div className="bg-card rounded-2xl border border-border p-6 flex flex-col">
            <p className="text-sm font-medium text-muted-foreground mb-4">Current Tier</p>

            {/* Badge — centered, vertical layout */}
            <div className="flex flex-col items-center gap-3 mb-5">
              <div className={`flex flex-col items-center gap-2 px-6 py-4 rounded-xl ${tier.bg} ${tier.border} border w-full`}>
                <Award className={`w-10 h-10 ${tier.color}`} />
                <span className={`text-2xl font-bold ${tier.color}`}>{tier.label}</span>
                <span className="text-xs text-muted-foreground">Score range: {tier.min}–{tier.max}</span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Progress to {nextTierInfo.next}</span>
                  <span className="font-medium text-foreground">
                    {nextTierInfo.needed > 0 ? `${nextTierInfo.needed} pts needed` : "Achieved!"}
                  </span>
                </div>
                <Progress value={nextTierInfo.progress} className="h-2" />
              </div>
            </div>

            {/* Lead Priority section pushed to bottom */}
            <div className="mt-auto pt-4 border-t border-border">
              <p className="text-sm font-medium text-foreground mb-3">Lead Priority by Tier</p>
              <div className="grid grid-cols-4 gap-2 text-center">
                {(["Bronze", "Silver", "Gold", "Elite"] as const).map((t) => {
                  const conf = tierConfig[t];
                  const multiplier = t === "Bronze" ? "0.9x" : t === "Silver" ? "1.0x" : t === "Gold" ? "1.1x" : "1.2x";
                  const isActive = profile.tier === t;
                  return (
                    <div
                      key={t}
                      className={`rounded-lg p-2 text-xs ${isActive ? `${conf.bg} ${conf.border} border font-semibold` : "text-muted-foreground"}`}
                    >
                      <span className={isActive ? conf.color : ""}>{conf.label}</span>
                      <p className={`text-xs mt-0.5 ${isActive ? "opacity-80" : ""}`}>{multiplier}</p>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Higher tiers mean leads become available to you sooner.
              </p>
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((metric) => (
            <div key={metric.label} className="bg-card rounded-2xl border border-border p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                  metric.status === "good" ? "bg-green-500/10" : metric.status === "neutral" ? "bg-muted/50" : "bg-orange-500/10"
                }`}>
                  <metric.icon className={`w-4 h-4 ${
                    metric.status === "good" ? "text-green-600" : metric.status === "neutral" ? "text-muted-foreground" : "text-orange-600"
                  }`} />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground">{metric.label}</p>
                  <p className="text-lg font-bold text-foreground">{metric.value}</p>
                </div>
              </div>
              <Progress value={metric.progress} className="h-1.5 mb-2" />
              <p className="text-xs text-muted-foreground">
                Benchmark: <span className="font-medium">{metric.benchmark}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-1">{metric.detail}</p>
              <p className="text-xs italic text-muted-foreground/70 mt-2 leading-relaxed">{metric.hint}</p>
            </div>
          ))}
        </div>

        {/* Why This Matters — motivational banner */}
        <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5">
          <div className="flex gap-4">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <ShieldCheck className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-foreground text-sm">Why does your Performance Score matter?</h3>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                At Trusted James, we connect consumers with agents who will genuinely help them.
                Your score reflects how actively you're working your leads — responding quickly, following up,
                and closing deals. <span className="text-foreground font-medium">Every agent receives their contracted leads regardless of score.</span> But
                agents with higher scores get <span className="text-foreground font-medium">priority access when additional leads become available</span>,
                and can earn faster delivery pacing over time.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Your score is based on the last 30 days only — so any improvement you make is reflected quickly.
              </p>
            </div>
          </div>
        </div>

        {/* How to Improve */}
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border bg-primary/5">
            <h2 className="font-display text-lg font-semibold text-foreground flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-accent" />
              How to Improve Your Score
            </h2>
            <p className="text-sm text-muted-foreground">Actions that positively impact your performance</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
            {tips.map((tip, index) => (
              <div key={index} className="flex gap-4 p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors">
                <div className="w-9 h-9 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <tip.icon className="w-4 h-4 text-accent" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{tip.title}</p>
                  <p className="text-sm text-muted-foreground mt-1">{tip.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-muted-foreground text-center">
          Performance is calculated from your lead activity over the last 30 days.
          Tier and score update automatically — improve your metrics to climb tiers and receive leads faster.
        </p>
      </div>
    </DashboardLayout>
  );
};

export default Performance;
